import { Tooltip } from '@material-ui/core';
import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { Route, useHistory } from 'react-router-dom';
import { getUsers } from '../AppShell/Authentication/AuthorisationAsync';
import { UserIUser } from '../AppShell/Authentication/AuthorisationModels';
import { CrossCloseIcon, RobotIcon, TickIcon } from '../Components/Icons';
import { ItemsLineCutter } from '../Components/ItemsLineCutter';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { LoadingPlaceholder, Placeholder } from '../Components/Placeholder';
import { IColumnInfo, SimpleDataTable } from '../Components/SimpleDataTable/SimpleDataTable';
import { useAsync } from '../Hooks/useAsync';
import { useDebounce } from '../Hooks/useDebounce';
import { getUserRoute, routeForNewUser, routeForUser, UserRouteParams } from '../Routes';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { standardName } from '../Shared/StringUtilities';
import { iconLight, successVariant } from '../Styles/Colours';
import { UserFile } from './UserFile';
import styles from './UserList.module.scss';

const columnConfig: IColumnInfo<UserIUser>[] = [
    {
        key: 'familyName',
        title: 'Full name',
        sort: ['familyName'],
        render: (row: UserIUser) => standardName({ givenNames: row.givenNames, familyName: row.familyName })
    },
    {
        key: 'username',
        title: 'User name',
        sort: ['username'],
        render: (row: UserIUser) => row.username || ''
    },
    {
        key: 'enabled',
        title: 'Status',
        sort: ['enabled'],
        width: 150,
        render: function renderGroup(row: UserIUser) {
            if (row.enabled) {
                return (
                    <div className={styles.statusHolder}>
                        <TickIcon size={'24'} className={styles.statusIcon} color={successVariant} />
                        <span>Enabled</span>
                    </div>
                );
            } else {
                return (
                    <div className={styles.statusHolder}>
                        <CrossCloseIcon size={'24'} className={styles.statusIcon} color={iconLight} />
                        <span className={styles.stopped}>Disabled</span>
                    </div>
                );
            }
        }
    },
    {
        key: 'groups',
        title: 'Groups',
        render: function renderGroup(row: UserIUser) {
            return (
                <ItemsLineCutter
                    items={row.groups}
                    renderItem={item => <div className={styles.chipGroup}>{item.name}</div>}
                    formatOverflowCount={hidden => (
                        <Tooltip
                            title={
                                <>
                                    {row.groups
                                        .slice(row.groups.length - hidden)
                                        .map(group => group.name)
                                        .join(', ')}
                                </>
                            }
                        >
                            <div className={styles.chipGroup}>+{hidden}</div>
                        </Tooltip>
                    )}
                    join={<div>&nbsp;</div>}
                />
            );
        }
    }
];

export function UserList(props: { searchTerm: string }): React.ReactElement {
    const searchTerm = props.searchTerm;
    const history = useHistory();
    const debouncedSearchTerm = useDebounce(searchTerm);
    const httpConfig = useHttpConfig();

    const userAsync = useAsync({
        defaultValue: [],
        httpConfig,
        getApi: getUsers
    });

    function refreshUsers() {
        userAsync.getApi(searchTerm);
    }

    useEffect(() => {
        refreshUsers();
    }, [debouncedSearchTerm]);

    const openNewUserDialog = () => history.push(routeForNewUser);

    const onRowSelection = (rows: UserIUser[]) => {
        if (rows && rows.length) {
            history.push(getUserRoute(rows[0].id));
        }
    };

    const renderUserFile = (routerProps: RouteComponentProps<UserRouteParams>) => (
        <UserFile key='user-file' {...routerProps} onDataChanged={refreshUsers} />
    );

    function getResults() {
        if (userAsync.loadCount === 0 || userAsync.data === null)
            return (
                <LayoutCentral>
                    <LoadingPlaceholder />
                </LayoutCentral>
            );

        if (userAsync.loadCount > 0 && userAsync.data.length <= 0 && searchTerm === '') {
            return (
                <Placeholder
                    icon={<RobotIcon />}
                    actionText={'Create User'}
                    action={openNewUserDialog}
                    infoText={'Whoops! There are no users in the system. Would you like to:'}
                />
            );
        } else if (userAsync.loadCount > 0 && userAsync.data.length <= 0) {
            return (
                <Placeholder
                    icon={<RobotIcon />}
                    actionText={'Create User'}
                    action={openNewUserDialog}
                    infoText={'Whoops! We could not find any records. Would you like to:'}
                />
            );
        }

        return (
            <SimpleDataTable<UserIUser>
                data={userAsync.data}
                enableScrollTo
                onRowSelection={onRowSelection}
                columns={columnConfig}
                selectedRows={[]}
            />
        );
    }

    const results = getResults();

    return (
        <div className={styles.flex}>
            {results}
            <Route path={routeForUser} render={renderUserFile} />
        </div>
    );
}
